import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    addConditionalSplitSends,
    removeBranchDescendants,
    removeConditionalSplit,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { getEffectiveSplitType } from 'domains/campaigns/components/CustomCampaign/utils';
import { CampaignStatus } from 'models/enums';
import useQueryParams from 'hooks/useQueryParams';
import { getSteps, MAX_SENDS_AMOUNT, renderChooseStepsForm } from '../utils';
import { SplitTypes } from 'domains/campaigns/types';

type Props = {
    activeSendIndex: number;
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

const CustomCampaignChooseSendTypeStep = ({
    activeSendIndex,
    campaignState,
    dispatchCampaignAction,
    setActiveStep,
}: Props) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const { editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag } = useFlags();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    const isEditingPausedCampaign = () => hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    const activeSend = campaignState.sends[activeSendIndex] as any;
    const effectiveSplitType = getEffectiveSplitType(activeSend, campaignState.sends);
    const isInsideConditionalSplit = !!effectiveSplitType;

    const parentSend = activeSend?.parentIndex != null ? (campaignState.sends[activeSend.parentIndex] as any) : null;
    const parentEffectiveSplitType = parentSend ? getEffectiveSplitType(parentSend, campaignState.sends) : undefined;
    const isBranchRoot = !!activeSend?.splitType && parentEffectiveSplitType !== activeSend?.splitType;

    const getIsStepDisabled = (step: CustomCampaignSteps) => {
        // For paused curations: enable only the option that matches the current send type
        if (isEditingPausedCampaign()) {
            const isConditionalSplit = !!activeSend?.splitAction && !!activeSend?.splitType;
            const isIndependentEmail = !isConditionalSplit;

            switch (step) {
                case CustomCampaignSteps.SELECT_CONTENT:
                    return !isIndependentEmail;
                case CustomCampaignSteps.SET_TIME_DELAY:
                    return true;
                case CustomCampaignSteps.CONDITIONAL_SPLIT:
                    if (isInsideConditionalSplit && !isBranchRoot) return true;
                    return !isConditionalSplit;
                case CustomCampaignSteps.SCHEDULE_CURATION:
                    return false;
                default:
                    return false;
            }
        }

        // Default logic for non-paused curations
        switch (step) {
            case CustomCampaignSteps.SELECT_CONTENT:
                return false;
            case CustomCampaignSteps.SET_TIME_DELAY:
                return true;
            case CustomCampaignSteps.CONDITIONAL_SPLIT:
                return isInsideConditionalSplit && !isBranchRoot;
            case CustomCampaignSteps.SCHEDULE_CURATION:
                return true;
            default:
                return false;
        }
    };

    const handleStepClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const step = (event.target as HTMLInputElement).value as CustomCampaignSteps;

        if (getIsStepDisabled(step)) return;

        setSelectedStep(step);

        if (step === CustomCampaignSteps.SELECT_CONTENT && isBranchRoot) {
            dispatchCampaignAction(removeConditionalSplit(activeSendIndex));
        }

        if (step === CustomCampaignSteps.CONDITIONAL_SPLIT) {
            const doesNotMeetSendExists = campaignState.sends.some(
                (send: any, idx: number) =>
                    idx !== activeSendIndex &&
                    send.splitType === SplitTypes.DOES_NOT_MEET &&
                    (send.parentIndex ?? 0) === (activeSend?.parentIndex ?? 0)
            );

            if (!doesNotMeetSendExists) {
                dispatchCampaignAction(removeBranchDescendants(activeSendIndex));
                dispatchCampaignAction(addConditionalSplitSends(activeSendIndex));
            }
        }

        setActiveStep(step);
    };

    const maxSendsReached = campaignState.sends.length === MAX_SENDS_AMOUNT;
    const steps = getSteps(
        getIsStepDisabled,
        maxSendsReached,
        isEditingPausedCampaign(),
        'Conditional splits cannot be nested inside an existing split branch.'
    );

    return renderChooseStepsForm(handleStepClick, selectedStep, steps);
};

export default CustomCampaignChooseSendTypeStep;
